import { createSelector } from "reselect";
import { getLicenseExpirationStatus, ONE_DAY_IN_MILLISECONDS, ONE_SECOND_IN_MILLISECONDS, } from "utils/dateTime";
export const licensingStateSelector = (state) => state.licensing;
export const isLicensingModalOpenSelector = createSelector(licensingStateSelector, (state) => state.licensingModalOpen);
export const licensingModalTypeSelector = createSelector(licensingStateSelector, (state) => state.licensingModalType);
export const licensingSerialNumberSelector = createSelector(licensingStateSelector, (state) => state.serialNumber);
export const getServerErrorText = (error) => {
    if (error === null) {
        return "";
    }
    const { data: { errors = [] } = {} } = error;
    const { text } = errors[0] || {};
    return text;
};
export const licenseFileServerErrorSelector = createSelector(licensingStateSelector, (state) => getServerErrorText(state.serverError));
export const deactivationFinishedStatusSelector = createSelector(licensingStateSelector, (state) => state.deactivationFinished);
export const capabilityRequestErrorSelector = createSelector(licensingStateSelector, (state) => getServerErrorText(state.capabilityRequestError));
export const licensesListSelector = createSelector(licensingStateSelector, (state) => state.licenses || []);
export const totalPossibleSeatsSelector = createSelector(licensesListSelector, (licenses) => {
    return licenses
        .filter((license) => {
        const expirationStatus = getLicenseExpirationStatus(license);
        return expirationStatus === "active" || expirationStatus === "expires";
    })
        .reduce((acc, license) => acc + license.count, 0);
});
export const noLicensesSelector = createSelector(licensesListSelector, (licenses) => !licenses || licenses.length === 0);
export const showExpiredStatusSelector = createSelector(noLicensesSelector, licensesListSelector, (hasNoLicenses, licenses) => !hasNoLicenses && licenses.every((license) => getLicenseExpirationStatus(license) === "expired"));
const activeLicensesSelector = createSelector(licensesListSelector, (licenses) => licenses.filter((license) => getLicenseExpirationStatus(license) === "active"));
const licensesThatExpireSelector = createSelector(licensesListSelector, (licenses) => licenses.filter((license) => getLicenseExpirationStatus(license) === "expires"));
export const noActiveLicensesSelector = createSelector(activeLicensesSelector, licensesThatExpireSelector, (activeLicenses, expiresLicenses) => ![...activeLicenses, ...expiresLicenses].length);
export const futureLicensesSelector = createSelector(licensesListSelector, (licenses) => licenses.filter((license) => getLicenseExpirationStatus(license) === "future"));
const nearestFutureLicenseSelector = createSelector(futureLicensesSelector, (licenses) => licenses.sort((a, b) => new Date(a.start || 0).valueOf() - new Date(b.start || 0).valueOf())[0]);
const oldestLicenseThatExpireSelector = createSelector(licensesThatExpireSelector, (licenses) => licenses.sort((a, b) => new Date(b.end).valueOf() - new Date(a.end).valueOf())[0]);
const hasNextActiveLicenseSelector = createSelector(oldestLicenseThatExpireSelector, nearestFutureLicenseSelector, (oldestLicenseThatExpire, nearestFutureLicense) => {
    if (!oldestLicenseThatExpire || !nearestFutureLicense) {
        return false;
    }
    else {
        return (new Date(oldestLicenseThatExpire.end).getTime() + ONE_SECOND_IN_MILLISECONDS ===
            new Date(nearestFutureLicense.start || 0).getTime());
    }
});
export const footerPanelLicenseExpirationInfoSelector = createSelector(showExpiredStatusSelector, activeLicensesSelector, licensesThatExpireSelector, futureLicensesSelector, nearestFutureLicenseSelector, oldestLicenseThatExpireSelector, hasNextActiveLicenseSelector, (showExpiredStatus, activeLicenses, licensesThatExpire, futureLicenses, nearestFutureLicense, oldestLicenseThatExpire, hasNextActiveLicense) => {
    if (showExpiredStatus) {
        return {
            status: "expired",
            label: "Expired license",
        };
    }
    else if (activeLicenses.length > 0 || hasNextActiveLicense) {
        return {
            status: "active",
            label: "Active license",
        };
    }
    else if (licensesThatExpire.length > 0 && activeLicenses.length === 0) {
        const daysToExpire = Math.floor(Math.abs(new Date(oldestLicenseThatExpire.end).getTime() - new Date().getTime()) /
            ONE_DAY_IN_MILLISECONDS);
        return {
            status: "expires",
            label: `License expires in ${daysToExpire} day(s)`,
        };
    }
    else if (futureLicenses.length > 0 && activeLicenses.length === 0) {
        const daysToStart = Math.floor(Math.abs(new Date(nearestFutureLicense.start || 0).getTime() - new Date().getTime()) /
            ONE_DAY_IN_MILLISECONDS);
        return {
            status: "future",
            label: `License will be activated in ${daysToStart} day(s)`,
        };
    }
    else {
        return {
            status: "expired",
            label: "Expired license",
        };
    }
});
export const remainingSeatsSelector = createSelector(licensingStateSelector, (state) => state.seats.availableSeats);
export const isLastSeatSelector = createSelector(licensingStateSelector, (state) => state.seats.isLastSeat);
export const noSeatsAvailableSelector = createSelector(remainingSeatsSelector, isLastSeatSelector, (availableSeats, isLastSeat) => availableSeats <= 0 && !isLastSeat);
export const noSeatsAvailableToastVisibleSelector = createSelector(licensingStateSelector, (state) => state.noSeatsAvailableToastVisible);
export const seatToastVisibleStatusSelector = createSelector(licensingStateSelector, (state) => state.seatToastVisibleStatus);
